import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import moment from 'moment';

import { AppConfig } from '../../../app.settings';
import { LicenseService } from '../http-services/license.service';

import { AppConstants } from '../../../app.constants';
import { ForgotPasswordViewModel } from '../../models/view-models/account-models/forgot-password-view-model.type';
import { ResetPasswordViewModel } from '../../models/view-models/account-models/restore-password-view-model.type';
import { ConfirmEmailViewModel } from '../../models/view-models/account-models/confirm-email-view-model.type';
import { ChangePasswordViewModel } from '../../models/view-models/account-models/change-password-view-model.type';
import { UpdateProfileViewModel } from '../../models/view-models/account-models/user-models/update-profile-view-model.type';
import { DisplayAccountViewModel } from '../../models/view-models/account-models/user-models/display-account-view-model.type';
import { PersonalDataViewModel } from '../../models/view-models/account-models/user-models/personal-data-view-model';
import { IQRCode } from '../../models/view-models/account-models/qr-code.interface';
import { ValidateTwoFactorAuthenticationModel } from '../../models/view-models/account-models/validate-two-factor-authentication.model';
import { SetTwoFactorAuthModel } from '../../models/view-models/account-models/set-two-factor-auth.model';
import { UserCredentialsViewModel } from '../../models/view-models/account-models/user-models/user-credentials-view.model';
import { AuthDataInterface } from '../../models/view-models/account-models/auth-data.interface';
import { TokenStatusModel } from '../../models/view-models/account-models/user-models/token-status.model';
import { ChangeExpiredPasswordViewModel } from '../../models/view-models/account-models/change-expired-password.view.model';
import { ApiToken } from '../../models/view-models/account-models/user-models/api-token.model';
import { LegalConsentModel } from '../../models/view-models/account-models/legal-consent.model';

@Injectable()
export class AuthService {
  endpoint: string;
  userCredentials: BehaviorSubject<UserCredentialsViewModel> = new BehaviorSubject(null);

  constructor(
    public http: HttpClient,
    public config: AppConfig,
    public route: ActivatedRoute,
    private readonly router: Router,
    private readonly licenseService: LicenseService
  ) {
    this.endpoint = this.config.getConfig('api') + 'Account';
  }

  get userId() {
    const authData = JSON.parse(localStorage.getItem(AppConstants.AUTH_DATA_NAME));
    if (authData != null && authData.userId) {
      return authData.userId;
    } else {
      return null;
    }
  }

  addUserApiToken(tokenName: string, userId: string): Observable<ApiToken> {
    const params: HttpParams = new HttpParams()
      .set('apiTokenName', tokenName)
      .set('userId', userId);

    return this.http.post<ApiToken>(this.endpoint + '/ApiToken', null, { params });
  }

  acceptLegalConsent(): Observable<void> {
    return this.http.post<void>(this.endpoint + '/AcceptLegalConsent', null);
  }

  authenticate(data: TokenStatusModel) {
    this.licenseService.licenseStatusModel$.next(null);
      setTimeout(() => {
        this.licenseService.initLicenseStatusSubscription();
      });

      const authData = this.updateToken(data);
      const userRoles: string[] = authData.roles.split(',');
      if (authData && authData.roles && userRoles.includes(AppConstants.PDF_ASSIGNER_ROLE)) {
        this.router.navigate(['/PdfAssigner']);
      } else {
        let returnUrl = this.route.snapshot.queryParamMap.get(AppConstants.QUERY_PARAM_RETURN_URL) || '/';
        this.router.navigate([returnUrl]);
      }
  }

  updateToken(data: TokenStatusModel): AuthDataInterface {
    const authData: AuthDataInterface = {
      userToken: data.access_token,
      refreshToken: data.refresh_token,
      expirationDate: moment().add(data.expires_in, 'seconds').toDate(),
      roles: data.roles,
      userId: data.UserId
    };
    localStorage.setItem(AppConstants.AUTH_DATA_NAME, JSON.stringify(authData));

    return authData;
  }

  userAuthentication(userName: string, password: string, code?: string): Observable<TokenStatusModel> {
    const params: HttpParams = new HttpParams()
      .set('userName', userName)
      .set('password', password)
      .set('grant_type', 'password')
      .set('code_digit', code)
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-urlencoded', 'No-Auth': 'True' });
    const url = this.config.getConfig('host') + 'token';

    return this.http.post<TokenStatusModel>(url, params.toString().replace(/\+/g, encodeURIComponent('+')), { headers });
  }

  refreshToken(refreshToken: string): Observable<TokenStatusModel> {
    const params: HttpParams = new HttpParams()
      .set('refresh_token', refreshToken)
      .set('grant_type', 'refresh_token');
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-urlencoded', 'No-Auth': 'True' });
    const url = this.config.getConfig('host') + 'token';

    return this.http.post<TokenStatusModel>(url, params.toString().replace(/\+/g, encodeURIComponent('+')), { headers })
      .pipe(
        tap(data => this.updateToken(data))
      );
  }

  getCaller(): Observable<DisplayAccountViewModel> {
    return this.http.get<DisplayAccountViewModel>(this.endpoint + '/GetCaller', {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    });
  }

  getUsersFilteredByEmail(AutocompleteUserNameStartsWith: string): Observable<PersonalDataViewModel[]> {
    let query = `${this.endpoint}/GetUsersFilteredByEmail`;
    if (AutocompleteUserNameStartsWith) {
      query = `${query}?emailFilter=${AutocompleteUserNameStartsWith}`;
    }
    return this.http.get<PersonalDataViewModel[]>(query);
  }

  getUserApiToken(userId: string): Observable<ApiToken[]> {
    const params: HttpParams = new HttpParams()
      .set('userId', userId);

    return this.http.get<ApiToken[]>(this.endpoint + '/UserApiTokens', { params });
  }

  generateTwoFactorAuthenticationCode(email: string): Observable<IQRCode> {
    const params: HttpParams = new HttpParams()
      .set('email', email);

    return this.http.get<IQRCode>(this.endpoint + '/GenerateTwoFactorAuthenticationCodes', { params });
  }

  forgotPasswordRequest(model: ForgotPasswordViewModel) {
    return this.http.post(this.endpoint + '/ForgotPassword', model,
      { headers: new HttpHeaders({ 'No-Auth': 'True' }) });
  }

  restorePassword(model: ResetPasswordViewModel) {
    return this.http.post(this.endpoint + '/ResetPassword', model,
      { headers: new HttpHeaders({ 'No-Auth': 'True' }) });
  }

  revokeUserApiToken(id: number): Observable<void> {
    const params: HttpParams = new HttpParams()
      .set('apiTokenId', id);

    return this.http.delete<void>(this.endpoint + '/ApiToken', { params });
  }

  updateProfile(model: UpdateProfileViewModel): Observable<DisplayAccountViewModel> {
    return this.http.post<DisplayAccountViewModel>(this.endpoint + '/UpdateProfile', model,
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) });
  }

  changePassword(model: ChangePasswordViewModel) {
    return this.http.post(this.endpoint + '/ChangePassword', model,
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) });
  }

  changeExpiredPassword(model: ChangeExpiredPasswordViewModel): Observable<void> {
    return this.http.post<void>(this.endpoint + '/ChangeExpiredPassword', model,
      { headers: new HttpHeaders({ 'No-Auth': 'True' }) });
  }

  confirmEmail(model: ConfirmEmailViewModel) {
    return this.http.post(this.endpoint + '/ConfirmEmail', model,
      { headers: new HttpHeaders({ 'No-Auth': 'True' }) });
  }

  logOut() {
    localStorage.removeItem(AppConstants.AUTH_DATA_NAME);
  }

  validateTwoFactorAuthenticationCode(model: ValidateTwoFactorAuthenticationModel): Observable<boolean> {
    return this.http.post<boolean>(this.endpoint + '/ValidateTwoFactorAuthenticationCode', model);
  }

  setTwoFactorEnabled(email: string, isEnabled: boolean): Observable<void> {
    const model = new SetTwoFactorAuthModel(email, isEnabled);

    return this.http.post<void>(this.endpoint + '/SetTwoFactorEnabled', model);
  }

  getLegalConsent(): Observable<LegalConsentModel> {
    const headers = new HttpHeaders({ 'No-Auth': 'True' });
    return this.http.get<LegalConsentModel>(this.endpoint + '/LegalConsent', { headers });
  }

  setLegalConsent(model: LegalConsentModel): Observable<void> {
    return this.http.post<void>(this.endpoint + '/LegalConsent', model);
  }
}
